import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { usePositionContext } from './PositionContext';
import { useAccount } from './AccountContext';
import { useAccountType } from './AccountTypeContext';
import { useToast } from './ToastContext';

interface OrderContextType {
  selectedPrice: number | null;
  setSelectedPrice: (price: number) => void;
  clearSelectedPrice: () => void;
  showOrderConfirmation: boolean;
  setShowOrderConfirmation: (show: boolean) => void;
  orderDetails: OrderDetails | null;
  setOrderDetails: (details: OrderDetails | null) => void;
  executeOrder: (details?: OrderDetails) => { success: boolean; error?: string };
  showConfirmationPopup: boolean;
  setShowConfirmationPopup: (show: boolean) => void;
}

interface OrderDetails {
  side: 'buy' | 'sell';
  type: 'market' | 'limit' | 'stop';
  symbol: string;
  price: number;
  stopPrice?: number;
  amount: number;
  leverage: number;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrderContext must be used within an OrderProvider');
  }
  return context;
};

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(() => {
    // Load from localStorage on initialization
    const saved = localStorage.getItem('showConfirmationPopup');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Get position context functions
  let addOpenPosition: ((position: any) => void) | null = null;
  let addPendingOrder: ((order: any) => void) | null = null;
  
  try {
    const positionContext = usePositionContext();
    addOpenPosition = positionContext.addOpenPosition;
    addPendingOrder = positionContext.addPendingOrder;
  } catch (error) {
    // PositionContext not available, will handle gracefully
    console.warn('PositionContext not available in OrderProvider');
  }

  // Get account context for balance updates
  let accountBalance: any = null;
  let updateBalance: ((type: 'demo' | 'live', amount: number) => void) | null = null;
  
  try {
    const accountContext = useAccount();
    accountBalance = accountContext.accountBalance;
    updateBalance = accountContext.updateBalance;
  } catch (error) {
    // AccountContext not available, will handle gracefully
    console.warn('AccountContext not available in OrderProvider');
  }

  // Get account type context
  let accountType: 'demo' | 'live' = 'demo';
  try {
    const accountTypeContext = useAccountType();
    accountType = accountTypeContext.accountType;
  } catch (error) {
    // AccountTypeContext not available, will handle gracefully
    console.warn('AccountTypeContext not available in OrderProvider');
  }

  // Get toast context
  let showToast: ((message: string, type: 'success' | 'error' | 'info' | 'warning' | 'buy' | 'sell') => void) | null = null;
  try {
    const toastContext = useToast();
    showToast = toastContext.showToast;
  } catch (error) {
    // ToastContext not available, will handle gracefully
    console.warn('ToastContext not available in OrderProvider');
  }

  // Save to localStorage whenever the setting changes
  useEffect(() => {
    localStorage.setItem('showConfirmationPopup', JSON.stringify(showConfirmationPopup));
  }, [showConfirmationPopup]);

  const clearSelectedPrice = () => {
    setSelectedPrice(null);
  };

  // Calculate margin requirement for a trade
  const calculateMarginRequirement = (price: number, amount: number, leverage: number): number => {
    const tradeValue = price * amount;
    const marginRequired = tradeValue / leverage;
    return marginRequired;
  };

  const executeOrder = (details?: OrderDetails): { success: boolean; error?: string } => {
    const orderToExecute = details || orderDetails;
    if (orderToExecute) {
      // Here you would implement the actual order execution logic
      
      // Calculate margin requirement
      const marginRequired = calculateMarginRequirement(
        orderToExecute.price, 
        orderToExecute.amount, 
        orderToExecute.leverage
      );
      
      // Check if sufficient balance for margin requirement
      const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
      if (marginRequired > currentBalance) {
        console.error('Insufficient balance for trade');
        return { 
          success: false, 
          error: `Insufficient margin. Required: $${marginRequired.toFixed(2)}, Available: $${currentBalance.toFixed(2)}` 
        };
      }
      
      // Handle different order types
      if (orderToExecute.type === 'market') {
        // Market orders execute immediately and go to open positions
        console.log('OrderContext: Creating market order with leverage:', orderToExecute.leverage);
        if (addOpenPosition) {
          addOpenPosition({
            symbol: orderToExecute.symbol,
            type: orderToExecute.side === 'buy' ? 'Buy' : 'Sell',
            size: orderToExecute.amount.toString(),
            entry: orderToExecute.price.toString(),
            current: orderToExecute.price.toString(),
            pnl: '$0.00',
            pnlPercent: '0.00%',
            leverage: orderToExecute.leverage,
          });
          
          // Deduct margin from account balance for market orders
          if (updateBalance && accountBalance) {
            const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
            const newBalance = currentBalance - marginRequired;
            updateBalance(accountType, newBalance);
          }
        }
      } else {
        // Limit and stop orders go to pending orders
        if (addPendingOrder) {
          addPendingOrder({
            symbol: orderToExecute.symbol,
            type: orderToExecute.side === 'buy' ? 'Buy' : 'Sell',
            size: orderToExecute.amount.toString(),
            price: orderToExecute.price.toString(),
            stopPrice: orderToExecute.stopPrice?.toString(),
            orderType: orderToExecute.type,
            status: 'Pending',
            leverage: orderToExecute.leverage,
          });
          
          // For pending orders, we might want to reserve the margin but not deduct it yet
          // This depends on the broker's policy. For now, we'll deduct it immediately
          if (updateBalance && accountBalance) {
            const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
            const newBalance = currentBalance - marginRequired;
            updateBalance(accountType, newBalance);
          }
        }
      }
      
      // Clear the order details and close confirmation
      setOrderDetails(null);
      setShowOrderConfirmation(false);
      clearSelectedPrice();
      
      return { success: true }; // Order executed successfully
    }
    
    return { success: false, error: 'No order details' }; // No order details
  };

  const value: OrderContextType = {
    selectedPrice,
    setSelectedPrice,
    clearSelectedPrice,
    showOrderConfirmation,
    setShowOrderConfirmation,
    orderDetails,
    setOrderDetails,
    executeOrder,
    showConfirmationPopup,
    setShowConfirmationPopup,
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};
